import { SagaIterator } from 'redux-saga';
import { call, select, take, takeEvery } from 'redux-saga/effects';
import {
  AngebotsId,
  GlobalActions,
  GlobalPut,
  ModalCloseReason,
  ModalType,
} from '@ps20-frontend/shop-integration-redux';
import isEqual from 'lodash/isEqual';
import {
  App,
  compareProductIdsIfExistingAndSpLaufnrNotExists,
  FEVorvertragsnummer,
} from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DashboardAction, DashboardActions } from '../dashboard.actions';
import { IAppState } from '../appState';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { isOwnedByTgv } from '../selectors/tiergruppeWarenkorb.selector';
import { angebotIsTarifiert } from '../selectors/warenkorb.selector';

export function* watchDeleteAngebotRequested(): SagaIterator {
  yield takeEvery(
    DashboardActions.DELETE_ANGEBOT_REQUESTED,
    safeSaga(deleteAngebotRequestedHandler, App.SHOP_DASHBOARD),
  );
}

export function* deleteAngebotRequestedHandler(action: DashboardAction<WarenkorbAngebot>): SagaIterator {
  const state: IAppState = yield select();
  const produktFromAction: WarenkorbAngebot = action.payload;

  if (hasMultipleProductsWithSameVertragsnummer(produktFromAction, state.warenkorb)) {
    yield call(deleteAngeboteAfterUserConfirmed, produktFromAction, state.warenkorb);
  } else if (
    shouldShowWarningDialog(produktFromAction) ||
    shouldShowTiergruppeWarningDialog(produktFromAction, state.warenkorb)
  ) {
    yield call(deleteSingleAngebotAfterUserConfirmed, produktFromAction);
  } else {
    yield call(dispatchEventToDeleteSingleAngebot, produktFromAction);
  }
  const produktDeletedAction = new GlobalActions().removeObjectWithAuslaendischeAdresse(
    produktFromAction.appId,
    produktFromAction.angebotsId,
  );

  yield call(GlobalPut.getInstance().put, produktDeletedAction);
}

export function hasMultipleProductsWithSameVertragsnummer(
  produktFromAction: WarenkorbAngebot,
  produkte: Array<WarenkorbAngebot> = [],
): boolean {
  return produktFromAction.vorvertragsNummern?.some(
    vertragsnummerPresentInMultipleAngeboten(produkte),
  );
}

function vertragsnummerPresentInMultipleAngeboten(
  produkte: Array<WarenkorbAngebot>,
): (vertragsnummer: FEVorvertragsnummer) => boolean {
  return (vertragsnummer: FEVorvertragsnummer) => {
    return produkte.filter(produktShouldIncludeVertragsnummer(vertragsnummer)).length > 1;
  };
}

function produktShouldIncludeVertragsnummer(
  vertragsnummer: FEVorvertragsnummer,
): (produkt: WarenkorbAngebot) => boolean {
  return (produkt: WarenkorbAngebot) =>
    produkt.vorvertragsNummern?.some(vertragsnummerInProdukt =>
      checkFeVorvertragsnummer(vertragsnummerInProdukt, vertragsnummer),
    );
}

function* deleteAngeboteAfterUserConfirmed(
  produktFromAction: WarenkorbAngebot,
  produkte: Array<WarenkorbAngebot>,
): SagaIterator {
  yield call(
    GlobalPut.getInstance().put,
    new GlobalActions().requestModal(ModalType.DELETE_WARNING_MULTIPLE_ANGEBOTE),
  );
  const closeModalAction = yield take(GlobalActions.CLOSE_MODAL);
  if (closeModalAction.payload === ModalCloseReason.PRIMARY) {
    yield call(dispatchEventToDeleteAngebote, produktFromAction, produkte);
  }
}

function* dispatchEventToDeleteAngebote(
  produktFromAction: WarenkorbAngebot,
  produkte: Array<WarenkorbAngebot>,
): SagaIterator {
  const angebotsIdsToDelete = getAngebotsIdsFromProductsWithSameVertragsnummer(
    produktFromAction,
    produkte,
  );
  const affectedVertragsnummern = collectVertragsnummernForAppIds(angebotsIdsToDelete, produkte);
  yield call(
    GlobalPut.getInstance().put,
    new GlobalActions().produktDeleted(angebotsIdsToDelete, affectedVertragsnummern),
  );
}

export function collectVertragsnummernForAppIds(
  appIdsFromProductsWithSameVertragsnummer: Array<AngebotsId>,
  produkte: Array<WarenkorbAngebot> = [],
): Array<FEVorvertragsnummer> {
  return produkte
    .filter(produkt =>
      appIdsFromProductsWithSameVertragsnummer.some(
        angebotsId =>
          produkt.appId === angebotsId.appId && produkt.angebotsId === angebotsId.angebotsId,
      ),
    )
    .map(produkt => produkt.vorvertragsNummern)
    .reduce((accumulator: Array<FEVorvertragsnummer>, currentValue: Array<FEVorvertragsnummer>) => {
      return accumulator.concat(removeDublicates(currentValue, accumulator));
    }, []);
}

function removeDublicates(
  currentValue: Array<FEVorvertragsnummer>,
  accumulator: Array<FEVorvertragsnummer>,
): Array<FEVorvertragsnummer> {
  return currentValue.filter(
    vorvertragsnummer =>
      !accumulator.some(accumulatorValue =>
        checkFeVorvertragsnummer(accumulatorValue, vorvertragsnummer),
      ),
  );
}

export function getAngebotsIdsFromProductsWithSameVertragsnummer(
  produktFromAction: WarenkorbAngebot,
  produkte: Array<WarenkorbAngebot> = [],
): AngebotsId[] {
  return produktFromAction.vorvertragsNummern
    ?.filter(vertragsnummerPresentInMultipleAngeboten(produkte))
    .map(getAngebotsIdsForGivenVertragsnummer(produkte))
    .reduce(reduceAngebotsIdsIntoOneArray(), []);
}

function getAngebotsIdsForGivenVertragsnummer(
  produkte: Array<WarenkorbAngebot>,
): (vertragsnummer: FEVorvertragsnummer) => { angebotsId: string; appId: App }[] {
  return (vertragsnummer: FEVorvertragsnummer) =>
    produkte
      .filter(produkt =>
        produkt.vorvertragsNummern?.some(vertragsnummerProdukt =>
          checkFeVorvertragsnummer(vertragsnummerProdukt, vertragsnummer),
        ),
      )
      .map(produkt => {
        return {
          appId: produkt.appId,
          angebotsId: produkt.angebotsId,
        };
      });
}

function reduceAngebotsIdsIntoOneArray(): (
  acc: Array<AngebotsId>,
  curVal: Array<AngebotsId>,
) => AngebotsId[] {
  return (acc: Array<AngebotsId>, curVal: Array<AngebotsId>) => acc.concat(curVal);
}

export function shouldShowWarningDialog(produkt: WarenkorbAngebot): boolean {
  return produkt.bruttopreis != null;
}

export function shouldShowTiergruppeWarningDialog(
  offerToDelete: WarenkorbAngebot,
  warenkorb: Array<WarenkorbAngebot> = [],
): boolean {
  return offerToDelete.appId === App.TGV && warenkorb.filter(isOwnedByTgv).some(angebotIsTarifiert);
}

function* deleteSingleAngebotAfterUserConfirmed(produktFromAction: WarenkorbAngebot): SagaIterator {
  yield call(
    GlobalPut.getInstance().put,
    new GlobalActions().requestModal(ModalType.DELETE_WARNING),
  );
  const closeModalAction = yield take(GlobalActions.CLOSE_MODAL);
  if (closeModalAction.payload === ModalCloseReason.PRIMARY) {
    yield* dispatchEventToDeleteSingleAngebot(produktFromAction);
  }
}

function* dispatchEventToDeleteSingleAngebot(produktFromAction: WarenkorbAngebot): SagaIterator {
  const produktDeletedAction = new GlobalActions().produktDeleted(
    [
      {
        appId: produktFromAction.appId,
        angebotsId: produktFromAction.angebotsId,
      },
    ],
    produktFromAction.vorvertragsNummern,
  );

  yield call(GlobalPut.getInstance().put, produktDeletedAction);
}

function checkFeVorvertragsnummer(
  vertragsnummer1: FEVorvertragsnummer,
  vertragsnummer2: FEVorvertragsnummer,
): boolean {
  return (
    isEqual(vertragsnummer1.vertragsnummer, vertragsnummer2.vertragsnummer) &&
    compareProductIdsIfExistingAndSpLaufnrNotExists(
      vertragsnummer1.produktIds,
      vertragsnummer2.produktIds,
      vertragsnummer1.spLaufnummer,
      vertragsnummer2.spLaufnummer,
    )
  );
}
